// Remote Operations (Workstream E) — pull-based Ansible operations over the install base.
//
// HQ enqueues a WHITELISTED operation for a specific install (or the whole fleet/cohort);
// each install PULLS its pending ops on its outbound check-in and runs them via its local
// runner (consent + maintenance gated), then reports status. The vendor never connects
// inbound. This page: dispatches an op (with live op-description + last-run context),
// lists the catalog (with a "Last run" column), and shows job history with filter tabs,
// duration, parsed result summaries, and an expandable raw-result drawer.
//
// v2 design ref: design_handoff_admin_ui_upgrade/screens/03-remote-ops-v2.html
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { apiRequest } from '@/lib/supabase';
import {
  Terminal, Send, RefreshCw, Info, Check, ChevronDown, ChevronUp,
  CheckCircle, XCircle, Clock, Loader2, AlertCircle,
  Circle, Server, Activity, Pause, AlertTriangle, Ban, ShieldAlert, ExternalLink,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmptyState } from '@/components/shared/EmptyState';
import { useToast } from '@/components/shared/ToastProvider';
import { CompanyChip } from '@/components/shared/CompanyChip';
import { TypedConfirmDialog } from '@/components/shared/TypedConfirmDialog';

// ── API contracts ──────────────────────────────────────────────────────
// The catalog's op identifier is `key` (operations_catalog.key); jobs reference it as `op_key`.
interface CatalogOp { key: string; description: string; playbook: string; enabled: boolean; }
interface Instance { instance_id: string; hostname: string | null; product_version: string | null; }
type ProgressPhase = 'pulled' | 'gated' | 'running' | 'reporting' | 'done' | 'failed';
interface Job {
  id: string; instance_id: string; op_key: string;
  status: 'pending' | 'claimed' | 'done' | 'failed';
  params: Record<string, unknown>; result: Record<string, unknown>;
  enqueued_at: string; claimed_at: string | null; reported_at: string | null;
  // How the op was initiated: HQ dispatch, host break-glass (out-of-band), or a host timer.
  source?: 'dispatch' | 'out-of-band' | 'scheduled';
  // Latest progress phase/step, annotated by the jobs listing (collapsed-row hint).
  progress_phase?: ProgressPhase | null; progress_step?: string | null;
  // Owning company (universal identity) — server-resolved via the license.
  organization_id?: string | null; organization_name?: string | null;
  // A20 C5 (migration 42). The report whose arrival caused this job to be
  // enqueued AUTOMATICALLY — null/absent for every job a human dispatched,
  // which is almost all of them. It exists so that a collect-support-bundle
  // appearing on a customer's box that nobody ordered is EXPLAINED rather than
  // merely present; an unexplained automatic op on someone else's machine is
  // exactly the thing an operator is right to distrust.
  support_ticket_id?: string | null; support_ticket_ref?: string | null;
}
// One entry in a job's append-only progress timeline (GET /operations/jobs/:id).
interface ProgressEvent {
  id: string; job_id: string; seq: number;
  phase: ProgressPhase; step: string | null;
  detail: Record<string, unknown>; at: string;
}
// One install's pull-runner heartbeat (GET /operations/runners). The pull IS the heartbeat.
interface RunnerStatus {
  instance_id: string; last_seen_at: string; poll_interval_s: number | null;
  runner_version: string | null; in_window: boolean | null;
  pending_count: number; updated_at: string;
}

// Spinner keyframes — injected once by the page so in-flight icons actually rotate
// (no global animate-spin utility is guaranteed here).
const SPIN_KEYFRAMES = '@keyframes vwspin { to { transform: rotate(360deg); } }';
const spinStyle: React.CSSProperties = { animation: 'vwspin 1s linear infinite' };

// ── Status pill skins ───────────────────────────────────────────────────
// Semantic tints from the v2 design ref (no exact token exists in index.css for
// these green/red/orange families; the design uses these literals directly).
// `skipped` is a DISPLAY-only status (the job row is status='done' but the op was a gated
// no-op — executed:false). We surface it distinctly so a no-op never reads as success.
type DisplayStatus = Job['status'] | 'skipped';
const STATUS_STYLE: Record<DisplayStatus, { bg: string; fg: string; icon: typeof CheckCircle }> = {
  done: { bg: 'var(--ps-pill-green-bg)', fg: 'var(--ps-pill-green-tx)', icon: CheckCircle },
  failed: { bg: 'var(--ps-pill-red-bg)', fg: 'var(--ps-pill-red-tx)', icon: XCircle },
  pending: { bg: 'var(--ps-pill-amber-bg)', fg: 'var(--ps-pill-amber-tx)', icon: Clock },
  claimed: { bg: 'var(--accent-light)', fg: 'var(--accent)', icon: Loader2 },
  skipped: { bg: 'var(--ps-pill-gray-bg)', fg: 'var(--ps-pill-gray-tx)', icon: Ban },
};

function StatusPill({ status, small }: { status: DisplayStatus; small?: boolean }) {
  const s = STATUS_STYLE[status];
  const Ico = s.icon;
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: small ? '2px 7px' : '3px 9px', borderRadius: 99,
        background: s.bg, color: s.fg, fontSize: small ? 10.5 : 11.5, fontWeight: 600,
      }}
    >
      <Ico size={small ? 10 : 11} style={status === 'claimed' ? spinStyle : undefined} />{status}
    </span>
  );
}

// ── Time helpers ─────────────────────────────────────────────────────────
function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const diff = Date.now() - t;
  const s = Math.floor(diff / 1000);
  if (s < 45) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

// "5/31 12:05 PM" style — used in dispatch bar + catalog Last-run column.
function shortStamp(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}/${d.getDate()} ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}

// Duration between enqueue and report → "21s" / "1m 4s". Returns null if not yet reported.
function jobDuration(j: Job): string | null {
  if (!j.reported_at) return null;
  const start = new Date(j.enqueued_at).getTime();
  const end = new Date(j.reported_at).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;
  const secs = Math.round((end - start) / 1000);
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

// ── Result parsing ─────────────────────────────────────────────────────────
// Our REAL result is a JSON OBJECT, typically { rc, result: {op, ...metrics}, playbook },
// e.g. backup → {op,size_mb,...}, vuln-scan → {scanner,critical,high,medium,low,...},
// ansible-shaped runs → {ok,changed,unreachable,failed}. We also tolerate the README's
// stringified form (result is/contains a raw string).
interface ParsedResult {
  rc: number | null;
  summary: string;          // concise one-line summary, e.g. "rc=0 · C:8 H:28 M:54 L:106"
  full: string;             // pretty raw for the drawer
  playbook: string | null;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && !Number.isNaN(v) ? v : null;
}

function parseJobResult(j: Job): ParsedResult | null {
  const raw = j.result;
  if (raw == null || (typeof raw === 'object' && Object.keys(raw).length === 0)) return null;

  // Tolerate a stringified result (README form): result is a JSON string or plain text.
  if (typeof raw === 'string') {
    let parsedObj: Record<string, unknown> | null = null;
    try { const p = JSON.parse(raw); if (p && typeof p === 'object') parsedObj = p as Record<string, unknown>; } catch { /* plain text */ }
    if (!parsedObj) {
      const firstLine = (raw as string).split('\n')[0] || (raw as string);
      return { rc: null, summary: firstLine, full: raw as string, playbook: null };
    }
    return parseObjectResult(parsedObj);
  }

  if (typeof raw === 'object') return parseObjectResult(raw as Record<string, unknown>);
  return { rc: null, summary: String(raw), full: String(raw), playbook: null };
}

function parseObjectResult(obj: Record<string, unknown>): ParsedResult {
  const rc = num(obj.rc);
  const playbook = typeof obj.playbook === 'string' ? obj.playbook : null;
  // Metrics may live at the top level or nested under `result` (our real shape).
  const inner = (obj.result && typeof obj.result === 'object' ? obj.result : obj) as Record<string, unknown>;
  const bits: string[] = [];

  // vuln-scan shape → "C:8 H:28 M:54 L:106"
  const crit = num(inner.critical), high = num(inner.high), med = num(inner.medium), low = num(inner.low);
  if (crit !== null || high !== null || med !== null || low !== null) {
    bits.push(`C:${crit ?? 0} H:${high ?? 0} M:${med ?? 0} L:${low ?? 0}`);
  }
  // backup shape → "size_mb=2.43"
  if (num(inner.size_mb) !== null) bits.push(`size_mb=${inner.size_mb}`);
  // ansible recap → "ok=5 changed=2"
  const ok = num(inner.ok), changed = num(inner.changed), unreachable = num(inner.unreachable), failed = num(inner.failed);
  if (ok !== null || changed !== null || unreachable !== null || failed !== null) {
    const recap = [
      ok !== null ? `ok=${ok}` : null,
      changed !== null ? `changed=${changed}` : null,
      unreachable ? `unreachable=${unreachable}` : null,
      failed ? `failed=${failed}` : null,
    ].filter(Boolean).join(' ');
    if (recap) bits.push(recap);
  }
  // stderr / error fallback (failures often carry a message and no metrics).
  if (bits.length === 0) {
    const errish = inner.stderr ?? inner.error ?? inner.message ?? obj.stderr ?? obj.error;
    if (typeof errish === 'string' && errish.trim()) bits.push(errish.split('\n')[0]);
  }

  const prefix = rc !== null ? `rc=${rc}` : null;
  const summary = [prefix, ...bits].filter(Boolean).join(' · ') || JSON.stringify(inner).slice(0, 80);
  return { rc, summary, full: JSON.stringify(obj, null, 2), playbook };
}

// ── Gated / skipped detection ─────────────────────────────────────────────
// A disruptive op (restart/upgrade/rotate-secrets) reports status='done' with
// executed:false when the runner refused it (gated no-op). Treat that as "skipped" so a
// no-op never shows green "done".
function gatedInfo(job: Job): { skipped: boolean; reason?: string } {
  const r = job.result as Record<string, unknown> | undefined;
  const inner = (r && typeof r.result === 'object' ? (r.result as Record<string, unknown>) : r) as Record<string, unknown> | undefined;
  if (inner && (inner.executed === false || inner.gated === true)) {
    return { skipped: true, reason: typeof inner.reason === 'string' ? inner.reason : undefined };
  }
  return { skipped: false };
}
function displayStatus(job: Job): DisplayStatus {
  return job.status === 'done' && gatedInfo(job).skipped ? 'skipped' : job.status;
}

// ── Vuln-scan findings (detailed remote-ops result) ───────────────────────
export interface Finding {
  cve?: string | null; package?: string | null; installed?: string | null;
  fixed?: string | null; severity?: string | null; title?: string | null; scanner?: string | null;
}
function jobFindings(job: Job): { findings: Finding[]; total: number; truncated: boolean } | null {
  const r = job.result as Record<string, unknown> | undefined;
  const inner = (r && typeof r.result === 'object' ? (r.result as Record<string, unknown>) : r) as Record<string, unknown> | undefined;
  if (!inner || !Array.isArray(inner.findings) || inner.findings.length === 0) return null;
  return {
    findings: inner.findings as Finding[],
    total: typeof inner.findings_total === 'number' ? inner.findings_total : (inner.findings as Finding[]).length,
    truncated: inner.findings_truncated === true,
  };
}

const SEV_STYLE: Record<string, { bg: string; fg: string }> = {
  critical: { bg: 'var(--ps-pill-red-bg)', fg: 'var(--ps-pill-red-tx)' },
  high: { bg: 'var(--ps-pill-amber-bg)', fg: 'var(--ps-pill-amber-tx)' },
  medium: { bg: 'var(--ps-pill-amber-bg)', fg: 'var(--ps-pill-amber-tx)' },
  low: { bg: 'var(--ps-pill-blue-bg)', fg: 'var(--ps-pill-blue-tx)' },
  unknown: { bg: 'var(--ps-pill-gray-bg)', fg: 'var(--ps-pill-gray-tx)' },
};
export function SeverityBadge({ sev }: { sev?: string | null }) {
  const k = (sev || 'unknown').toLowerCase();
  const s = SEV_STYLE[k] || SEV_STYLE.unknown;
  return (
    <span style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 99, background: s.bg, color: s.fg, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase' }}>{k}</span>
  );
}

// Shared CVE table — used by the vuln-scan job drawer and the Vulnerabilities page.
export function FindingsTable({ findings, showInstance }: { findings: (Finding & { instance_id?: string; organization_id?: string | null; organization_name?: string | null })[]; showInstance?: boolean }) {
  const headers = showInstance
    ? ['Severity', 'CVE', 'Package', 'Installed → Fixed', 'Instance', 'Title']
    : ['Severity', 'CVE', 'Package', 'Installed → Fixed', 'Title'];
  const th: React.CSSProperties = { textAlign: 'left', padding: '6px 10px', fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' };
  const td: React.CSSProperties = { padding: '6px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' };
  const mono: React.CSSProperties = { ...td, fontFamily: 'var(--mono, monospace)' };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr>{headers.map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {findings.map((f, i) => (
            <tr key={(f.cve || '') + (f.package || '') + i}>
              <td style={td}><SeverityBadge sev={f.severity} /></td>
              <td style={mono}>
                {f.cve && /^CVE-/i.test(f.cve)
                  ? <a href={`https://nvd.nist.gov/vuln/detail/${f.cve}`} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3 }}>{f.cve}<ExternalLink size={10} /></a>
                  : (f.cve || '—')}
              </td>
              <td style={mono}>{f.package || '—'}</td>
              <td style={mono}>
                <span style={{ color: 'var(--text-secondary)' }}>{f.installed || '?'}</span>{' → '}
                <span style={{ color: f.fixed ? 'var(--ps-pill-green-tx)' : 'var(--text-muted)', fontWeight: f.fixed ? 600 : 400 }}>{f.fixed || 'no fix'}</span>
              </td>
              {showInstance && (
                <td style={mono}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span>{f.instance_id || '—'}</span>
                    {f.organization_name && <span style={{ fontFamily: 'var(--font-sans, inherit)' }}><CompanyChip id={f.organization_id} name={f.organization_name} origin={{ type: 'vuln' }} size={11} weight={500} /></span>}
                  </div>
                </td>
              )}
              <td style={{ ...td, color: 'var(--text-secondary)', maxWidth: 360 }}>{f.title || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Styles (tokens-first; layout numerics mirror the v2 design ref) ─────────
const sectionCard: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
  boxShadow: 'var(--shadow-card)', marginBottom: 16,
};
const cardHeader: React.CSSProperties = {
  padding: '14px 20px', borderBottom: '1px solid var(--border)',
  display: 'flex', alignItems: 'center', gap: 8,
};
const cardTitle: React.CSSProperties = { fontSize: 14, fontWeight: 600 };
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '9px 16px', fontSize: 11, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)',
  background: 'var(--bg-muted)', height: 'auto', borderBottom: '1px solid var(--border)',
};
const btnSec: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  borderRadius: 8, background: 'var(--surface)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'inherit',
};

// Most-recent job (by enqueued_at) for a given op_key, across all instances.
function latestJobForOp(jobs: Job[], opKey: string): Job | null {
  let best: Job | null = null;
  for (const j of jobs) {
    if (j.op_key !== opKey) continue;
    if (!best || new Date(j.enqueued_at).getTime() > new Date(best.enqueued_at).getTime()) best = j;
  }
  return best;
}

// ── Runner health ────────────────────────────────────────────────────────
// The install's pull runner checks in on every poll (GET /operations/pending). A stale
// last_seen_at means the runner is down — so dispatched ops sit 'pending' with nothing
// to consume them. Health is derived from last_seen_at vs the runner's own poll cadence.
type RunnerHealth = 'live' | 'stale' | 'down';
function runnerHealth(r: RunnerStatus): { state: RunnerHealth; bg: string; fg: string } {
  const poll = r.poll_interval_s && r.poll_interval_s > 0 ? r.poll_interval_s : 20;
  const age = (Date.now() - new Date(r.last_seen_at).getTime()) / 1000;
  if (Number.isNaN(age)) return { state: 'down', bg: 'var(--ps-pill-red-bg)', fg: 'var(--ps-pill-red-tx)' };
  if (age <= poll * 2.5) return { state: 'live', bg: 'var(--ps-pill-green-bg)', fg: 'var(--ps-pill-green-tx)' };
  if (age <= poll * 10) return { state: 'stale', bg: 'var(--ps-pill-amber-bg)', fg: 'var(--ps-pill-amber-tx)' };
  return { state: 'down', bg: 'var(--ps-pill-red-bg)', fg: 'var(--ps-pill-red-tx)' };
}

// Map instance_id → its runner health (for the card + the stuck-job flag in Job History).
function runnerHealthByInstance(runners: RunnerStatus[]): Map<string, RunnerHealth> {
  const m = new Map<string, RunnerHealth>();
  for (const r of runners) m.set(r.instance_id, runnerHealth(r).state);
  return m;
}

function RunnerHealthCard({ runners }: { runners: RunnerStatus[] }) {
  const anyDown = runners.some((r) => runnerHealth(r).state === 'down');
  return (
    <div style={sectionCard}>
      <div style={cardHeader}>
        <Server size={15} color="var(--text-secondary)" />
        <span style={cardTitle}>Runner health</span>
        {anyDown && (
          <span style={{
            marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 11.5, fontWeight: 600, color: 'var(--ps-pill-red-tx)',
          }}>
            <AlertTriangle size={12} /> A runner is down — dispatched ops won't run
          </span>
        )}
      </div>
      {runners.length === 0 ? (
        <EmptyState
          icon="server"
          title="No runner check-ins yet"
          body="No install has pulled operations. A runner reports here on its first outbound check-in."
        />
      ) : (
        <div style={{ padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {runners.map((r) => {
            const h = runnerHealth(r);
            const label = h.state === 'live' ? 'Live' : h.state === 'stale' ? 'Stale' : 'Down';
            const Ico = h.state === 'live' ? Activity : h.state === 'stale' ? Clock : XCircle;
            return (
              <div key={r.instance_id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px',
                border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)',
              }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px',
                  borderRadius: 99, background: h.bg, color: h.fg, fontSize: 11.5, fontWeight: 600,
                }}>
                  <Ico size={11} style={h.state === 'live' ? spinStyle : undefined} />{label}
                </span>
                <span style={{ fontFamily: 'var(--mono, monospace)', fontSize: 12.5, fontWeight: 600 }}>
                  {r.instance_id}
                </span>
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                  checked in {relativeTime(r.last_seen_at)}
                </span>
                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14, fontSize: 11.5, color: 'var(--text-muted)' }}>
                  {r.poll_interval_s ? <span>polls every {r.poll_interval_s}s</span> : null}
                  {r.runner_version ? <span style={{ fontFamily: 'var(--mono, monospace)' }}>{r.runner_version}</span> : null}
                  {h.state === 'down' && <span style={{ color: 'var(--ps-pill-red-tx)', fontWeight: 600 }}>not checking in</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Dispatch panel ──────────────────────────────────────────────────────
function DispatchPanel({
  catalog, instances, jobs, onDispatched, initialTarget,
}: {
  catalog: CatalogOp[]; instances: Instance[]; jobs: Job[];
  onDispatched: () => void; initialTarget?: string;
}) {
  const { toast } = useToast();
  const enabledOps = useMemo(() => catalog.filter((o) => o.enabled), [catalog]);
  const [opKey, setOpKey] = useState('');
  const [target, setTarget] = useState(initialTarget || 'all'); // 'all' = whole fleet (cohort); else instance_id

  // Honour a deep-link from the Fleet page (?instance=<id>) once it arrives. Only
  // adopt it if it names a real install so a stale link can't pin a phantom target.
  useEffect(() => {
    if (initialTarget && initialTarget !== 'all' && instances.some((i) => i.instance_id === initialTarget)) {
      setTarget(initialTarget);
    }
  }, [initialTarget, instances]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  // Fleet-wide dispatch is confirmed by typing the operation key (DF-38).
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fleetWide = !target || target === 'all';

  // Default the operation once the catalog arrives.
  useEffect(() => {
    if (!opKey && enabledOps.length) setOpKey(enabledOps[0].key);
  }, [enabledOps, opKey]);

  const op = useMemo(() => catalog.find((o) => o.key === opKey) || null, [catalog, opKey]);
  const lastJob = useMemo(() => (opKey ? latestJobForOp(jobs, opKey) : null), [jobs, opKey]);

  // One install is a single step; the whole fleet needs the typed confirm first.
  const requestDispatch = () => {
    if (!opKey) return;
    if (fleetWide) setConfirmOpen(true);
    else void dispatch();
  };

  const dispatch = async () => {
    if (!opKey || busy) return;
    setBusy(true);
    try {
      const body: Record<string, unknown> = { op_key: opKey };
      if (target && target !== 'all') body.instance_id = target; else body.cohort = 'all';
      const r = await apiRequest<{ enqueued: number }>('/operations/enqueue', {
        method: 'POST', body: JSON.stringify(body),
      });
      const n = r?.enqueued ?? 0;
      setSent(true);
      setTimeout(() => setSent(false), 2000);
      toast({
        kind: 'success',
        title: `Dispatched '${opKey}'`,
        body: `Queued for ${n} install${n !== 1 ? 's' : ''}${target && target !== 'all' ? ` (${target})` : ' (whole fleet)'}. Delivered on next check-in.`,
      });
      onDispatched();
    } catch (e: unknown) {
      toast({ kind: 'error', title: 'Dispatch failed', body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div style={sectionCard}>
      <div style={cardHeader}>
        <Send size={15} color="var(--text-secondary)" />
        <span style={cardTitle}>Dispatch an operation</span>
      </div>
      <div style={{ padding: '18px 20px' }}>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55, marginBottom: 18 }}>
          Enqueue a whitelisted operation for one install or the whole fleet. The op is delivered on
          the install's next outbound check-in and runs via its local runner.{' '}
          <strong>The vendor never connects inbound.</strong>
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12, alignItems: 'start' }}>
          {/* Operation picker */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>
              Operation
            </label>
            <Select value={opKey} onValueChange={(v) => setOpKey(v)}>
              <SelectTrigger style={{ fontFamily: 'var(--mono, monospace)' }} aria-label="Operation">
                <SelectValue placeholder="Select an operation" />
              </SelectTrigger>
              <SelectContent>
                {enabledOps.map((o) => (
                  <SelectItem key={o.key} value={o.key} style={{ fontFamily: 'var(--mono, monospace)' }}>
                    {o.key}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Target picker */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>
              Target
            </label>
            <Select value={target} onValueChange={(v) => setTarget(v)}>
              <SelectTrigger aria-label="Target">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Whole fleet (all installs)</SelectItem>
                {instances.map((i) => (
                  <SelectItem key={i.instance_id} value={i.instance_id}>
                    {i.instance_id}{i.hostname ? ` (${i.hostname})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div style={{ paddingTop: 22 }}>
            <button
              onClick={requestDispatch}
              disabled={busy || !opKey}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px',
                borderRadius: 8, background: sent ? 'var(--status-active)' : 'var(--accent)', color: '#FFFFFF',
                border: 'none', fontSize: 13, fontWeight: 600,
                cursor: busy || !opKey ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1,
                fontFamily: 'inherit', transition: 'background .15s', whiteSpace: 'nowrap',
              }}
            >
              {sent ? <Check size={14} /> : <Send size={14} />}{sent ? 'Dispatched' : 'Dispatch'}
            </button>
          </div>
        </div>
        {/* Op-description bar: description left, last-run context right (reactive to op change). */}
        <div style={{
          marginTop: 14, padding: '12px 14px', background: 'var(--bg-muted)', borderRadius: 8,
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <Info size={14} color="var(--text-muted)" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, fontSize: 12.5, color: 'var(--text-secondary)' }}>
            {op?.description || 'Select an operation to dispatch.'}
          </div>
          {lastJob && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, whiteSpace: 'nowrap' }}>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)' }}>Last:</span>
              <StatusPill status={displayStatus(lastJob)} small />
              <span style={{ color: 'var(--text-muted)' }}>
                {shortStamp(lastJob.reported_at || lastJob.enqueued_at)}
              </span>
            </div>
          )}
        </div>
      </div>
      <TypedConfirmDialog
        open={confirmOpen}
        title={`Send '${opKey}' to the whole fleet?`}
        body={`This queues '${opKey}' for ${instances.length > 0 ? (instances.length === 1 ? '1 install' : `${instances.length} installs`) : 'every install'}; each runs it on its next check-in${op?.description ? ` (${op.description.replace(/\.$/, '')})` : ''}. To run it on one install, choose that install as the target instead.`}
        phrase={opKey}
        confirmLabel="Dispatch to all installs"
        busy={busy}
        onConfirm={dispatch}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}

// ── Operation catalog (whitelist) with Last-run column ──────────────────
function OpCatalog({ catalog, jobs }: { catalog: CatalogOp[]; jobs: Job[] }) {
  return (
    <div style={{ ...sectionCard, overflow: 'hidden' }}>
      <div style={cardHeader}><span style={cardTitle}>Operation catalog (whitelist)</span></div>
      {catalog.length === 0 ? (
        <EmptyState icon="terminal" title="No operations in the catalog" body="The whitelist is empty. No operations can be dispatched until the server defines them." />
      ) : (
        <Table style={{ borderCollapse: 'collapse' }}>
          <TableHeader>
            <TableRow>
              {['Operation', 'Playbook', 'Description', 'Last run', 'Enabled'].map((h) => (
                <TableHead key={h} style={thStyle}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {catalog.map((o) => {
              const last = latestJobForOp(jobs, o.key);
              return (
                <TableRow key={o.key}>
                  <TableCell style={{ padding: '11px 16px', fontFamily: 'var(--mono, monospace)', fontSize: 12.5, fontWeight: 600 }}>{o.key}</TableCell>
                  <TableCell style={{ padding: '11px 16px', fontFamily: 'var(--mono, monospace)', fontSize: 11.5, color: 'var(--text-secondary)' }}>{o.playbook}</TableCell>
                  <TableCell style={{ padding: '11px 16px', fontSize: 13, color: 'var(--text-secondary)' }}>{o.description}</TableCell>
                  <TableCell style={{ padding: '11px 16px' }}>
                    {last ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                        <StatusPill status={displayStatus(last)} small />
                        <span style={{ color: 'var(--text-muted)' }}>{shortStamp(last.reported_at || last.enqueued_at)}</span>
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: 12.5 }}>Never run</span>
                    )}
                  </TableCell>
                  <TableCell style={{ padding: '11px 16px', textAlign: 'center' }}>
                    {o.enabled
                      ? <Check size={15} color="var(--ps-pill-green-tx)" aria-label="enabled" />
                      : <XCircle size={15} color="var(--text-muted)" aria-label="disabled" />}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ── Per-job pipeline stepper ──────────────────────────────────────────────
// Renders the op's lifecycle as a vertical stepper from its progress timeline:
// Dispatched → Pulled → Gated → Running (+ live Ansible task substeps) → Reported.
// A stage is reached from progress events (or the job's own claimed/reported stamps);
// the last reached stage spins while the job is still in flight.
const PIPELINE_STAGES: { key: ProgressPhase | 'enqueued'; label: string }[] = [
  { key: 'enqueued', label: 'Dispatched (enqueued)' },
  { key: 'pulled', label: 'Pulled by runner' },
  { key: 'gated', label: 'Gate check (consent + maintenance window)' },
  { key: 'running', label: 'Running playbook' },
  { key: 'reporting', label: 'Reporting result' },
  { key: 'done', label: 'Reported' },
];

function StepIcon({ kind }: { kind: 'done' | 'active' | 'failed' | 'todo' | 'skipped' }) {
  if (kind === 'failed') return <XCircle size={15} color="var(--ps-pill-red-tx)" />;
  if (kind === 'skipped') return <Ban size={15} color="var(--ps-pill-gray-tx)" />;
  if (kind === 'done') return <CheckCircle size={15} color="var(--ps-pill-green-tx)" />;
  if (kind === 'active') return <Loader2 size={15} color="var(--accent)" style={spinStyle} />;
  return <Circle size={15} color="var(--text-muted)" />;
}

function JobStepper({ job, progress, loading }: { job: Job; progress: ProgressEvent[]; loading: boolean }) {
  const terminal = job.status === 'done' || job.status === 'failed';
  const gated = gatedInfo(job);
  const has = (p: ProgressPhase) => progress.some((e) => e.phase === p);
  const firstAt = (p: ProgressPhase) => progress.find((e) => e.phase === p)?.at ?? null;
  const runningSteps = progress.filter((e) => e.phase === 'running' && e.step);

  const reached: Record<string, boolean> = {
    enqueued: true,
    pulled: !!job.claimed_at || has('pulled'),
    gated: has('gated'),
    running: has('running'),
    reporting: has('reporting'),
    done: !!job.reported_at || terminal,
  };
  const timeOf: Record<string, string | null> = {
    enqueued: job.enqueued_at,
    pulled: job.claimed_at || firstAt('pulled'),
    gated: firstAt('gated'),
    running: firstAt('running'),
    reporting: firstAt('reporting'),
    done: job.reported_at || firstAt('done') || firstAt('failed'),
  };

  const reachedKeys = PIPELINE_STAGES.filter((s) => reached[s.key]).map((s) => s.key);
  const activeKey = !terminal && reachedKeys.length ? reachedKeys[reachedKeys.length - 1] : null;

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Activity size={13} color="var(--text-secondary)" />
        <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)' }}>Pipeline</span>
        {loading && <Loader2 size={11} color="var(--text-muted)" style={spinStyle} />}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {PIPELINE_STAGES.map((stage, i) => {
          const isReached = reached[stage.key];
          const isActive = activeKey === stage.key;
          const isFinalFailed = stage.key === 'done' && job.status === 'failed' && isReached;
          const isFinalSkipped = stage.key === 'done' && gated.skipped && isReached;
          const kind: 'done' | 'active' | 'failed' | 'todo' | 'skipped' =
            isFinalFailed ? 'failed' : isFinalSkipped ? 'skipped' : isActive ? 'active' : isReached ? 'done' : 'todo';
          const label = stage.key === 'done'
            ? (job.status === 'failed' ? 'Failed' : gated.skipped ? 'Skipped (gated)' : stage.label)
            : stage.label;
          const last = i === PIPELINE_STAGES.length - 1;
          return (
            <div key={stage.key}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <StepIcon kind={kind} />
                  {!last && <div style={{ width: 1, height: 14, background: isReached ? 'var(--ps-pill-green-tx)' : 'var(--border)' }} />}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, paddingBottom: last ? 0 : 6 }}>
                  <span style={{
                    fontSize: 12.5, fontWeight: isActive ? 700 : 500,
                    color: isReached ? 'var(--text-primary)' : 'var(--text-muted)',
                  }}>{label}</span>
                  {timeOf[stage.key] && (
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{relativeTime(timeOf[stage.key])}</span>
                  )}
                </div>
              </div>
              {/* Live Ansible task substeps under the Running stage. */}
              {stage.key === 'running' && runningSteps.length > 0 && (
                <div style={{ marginLeft: 25, marginBottom: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {runningSteps.map((s, si) => {
                    const lastStep = si === runningSteps.length - 1;
                    const stepActive = activeKey === 'running' && lastStep;
                    return (
                      <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11.5 }}>
                        {stepActive
                          ? <Loader2 size={11} color="var(--accent)" style={spinStyle} />
                          : <Check size={11} color="var(--ps-pill-green-tx)" />}
                        <span style={{ fontFamily: 'var(--mono, monospace)', color: 'var(--text-secondary)' }}>{s.step}</span>
                        <span style={{ color: 'var(--text-muted)' }}>{relativeTime(s.at)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Job history: filter tabs + duration + parsed result + expandable drawer ──
type Filter = 'all' | 'pending' | 'done' | 'skipped' | 'failed';

function JobHistory({ jobs, runners }: { jobs: Job[]; runners: RunnerStatus[] }) {
  const [filter, setFilter] = useState<Filter>('all');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [progressById, setProgressById] = useState<Record<string, ProgressEvent[]>>({});
  const [progressLoading, setProgressLoading] = useState<string | null>(null);

  const healthByInstance = useMemo(() => runnerHealthByInstance(runners), [runners]);

  // Fetch (and on each poll, refresh) the expanded job's full progress timeline so the
  // stepper streams live. Depends on `jobs` so a live tick re-pulls the open job.
  useEffect(() => {
    if (!expanded) return;
    let cancelled = false;
    setProgressLoading(expanded);
    apiRequest<Job & { progress: ProgressEvent[] }>(`/operations/jobs/${expanded}`)
      .then((r) => { if (!cancelled) setProgressById((m) => ({ ...m, [expanded]: r?.progress || [] })); })
      .catch(() => { if (!cancelled) setProgressById((m) => ({ ...m, [expanded]: m[expanded] || [] })); })
      .finally(() => { if (!cancelled) setProgressLoading((c) => (c === expanded ? null : c)); });
    return () => { cancelled = true; };
  }, [expanded, jobs]);

  const counts = useMemo(() => ({
    all: jobs.length,
    pending: jobs.filter((j) => j.status === 'pending' || j.status === 'claimed').length,
    // 'done' excludes gated no-ops so a skipped op never inflates the success count.
    done: jobs.filter((j) => displayStatus(j) === 'done').length,
    skipped: jobs.filter((j) => displayStatus(j) === 'skipped').length,
    failed: jobs.filter((j) => j.status === 'failed').length,
  }), [jobs]);

  const filtered = useMemo(() => {
    if (filter === 'all') return jobs;
    if (filter === 'pending') return jobs.filter((j) => j.status === 'pending' || j.status === 'claimed');
    return jobs.filter((j) => displayStatus(j) === filter);
  }, [jobs, filter]);

  const tabs: { id: Filter; label: string }[] = [
    { id: 'all', label: 'All' }, { id: 'pending', label: 'Pending' },
    { id: 'done', label: 'Done' }, { id: 'skipped', label: 'Skipped' }, { id: 'failed', label: 'Failed' },
  ];

  return (
    <div style={{ ...sectionCard, overflow: 'hidden', marginBottom: 0 }}>
      <div style={{ ...cardHeader, justifyContent: 'space-between' }}>
        <span style={cardTitle}>Job history</span>
        <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
          <TabsList style={{ height: 'auto' }}>
            {tabs.map((t) => {
              const c = counts[t.id];
              const isFailedHot = t.id === 'failed' && c > 0;
              return (
                <TabsTrigger key={t.id} value={t.id} style={{ gap: 5 }}>
                  {t.label}
                  <span style={{
                    padding: '1px 6px', borderRadius: 99, fontSize: 10,
                    fontWeight: isFailedHot ? 700 : 500,
                    background: isFailedHot ? 'var(--ps-pill-red-bg)' : 'var(--bg-muted)',
                    color: isFailedHot ? 'var(--ps-pill-red-tx)' : 'var(--text-muted)',
                  }}>{c}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon="inbox"
          title={filter === 'all' ? 'No operations dispatched yet' : `No ${filter} jobs`}
          body={filter === 'all'
            ? 'Dispatch an operation above and it will appear here once an install checks in.'
            : `There are no jobs with status "${filter}" right now.`}
        />
      ) : (
        <Table style={{ borderCollapse: 'collapse' }}>
          <TableHeader>
            <TableRow>
              {['Instance', 'Operation', 'Status', 'Duration', 'Time', 'Result', ''].map((h, i) => (
                <TableHead key={h || `c${i}`} style={thStyle}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((job) => {
              const parsed = parseJobResult(job);
              const isExp = expanded === job.id;
              const isFailed = job.status === 'failed';
              const failResult = parsed?.rc != null ? parsed.rc !== 0 : isFailed;
              const dur = jobDuration(job);
              // A job is "stuck" when it's waiting but its install's runner isn't checking
              // in (down or never seen) — the exact case where ops silently sit pending.
              const inFlight = job.status === 'pending' || job.status === 'claimed';
              const health = healthByInstance.get(job.instance_id);
              const isStuck = inFlight && (health === undefined || health === 'down');
              const tdBase: React.CSSProperties = {
                padding: '12px 16px', fontSize: 12.5,
                borderBottom: isExp ? 'none' : '1px solid var(--border)',
              };
              return (
                <React.Fragment key={job.id}>
                  <TableRow
                    onClick={() => setExpanded(isExp ? null : job.id)}
                    style={{ background: isExp ? 'var(--surface-sunken)' : 'var(--surface)', cursor: 'pointer' }}
                  >
                    <TableCell style={{ ...tdBase, fontFamily: 'var(--mono, monospace)', color: 'var(--text-primary)' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span>{job.instance_id}</span>
                        {job.organization_name && <span style={{ fontFamily: 'var(--font-sans, inherit)' }}><CompanyChip id={job.organization_id} name={job.organization_name} origin={{ type: 'job' }} size={11} weight={500} /></span>}
                      </div>
                    </TableCell>
                    <TableCell style={{ ...tdBase, fontFamily: 'var(--mono, monospace)' }}>{job.op_key}</TableCell>
                    <TableCell style={tdBase}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <StatusPill status={displayStatus(job)} />
                        {job.source && job.source !== 'dispatch' && (
                          <span title="Run on the host outside the dashboard dispatch flow"
                            style={{
                              padding: '2px 7px', borderRadius: 99, fontSize: 10, fontWeight: 600,
                              background: 'var(--bg-muted)', color: 'var(--text-muted)',
                            }}>{job.source}</span>
                        )}
                        {/* A20 C5 — the job→ticket half of the link, and the
                            answer to "why is this running?". A LINK, not a
                            label: the ticket is where the reason actually is. */}
                        {job.support_ticket_id && (
                          <Link
                            to={`/support/tickets/${job.support_ticket_id}`}
                            title="Requested automatically when this report arrived from the install"
                            style={{
                              padding: '2px 7px', borderRadius: 99, fontSize: 10, fontWeight: 600,
                              background: 'var(--bg-muted)', color: 'var(--text-muted)',
                              textDecoration: 'none',
                            }}>
                            {job.support_ticket_ref || 'report'}
                          </Link>
                        )}
                        {isStuck && (
                          <span title="The install's runner isn't checking in — this op can't be picked up."
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px',
                              borderRadius: 99, background: 'var(--ps-pill-red-bg)', color: 'var(--ps-pill-red-tx)', fontSize: 10.5, fontWeight: 600,
                            }}>
                            <AlertTriangle size={10} /> stuck — runner down
                          </span>
                        )}
                        {!isStuck && inFlight && job.progress_phase && (
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--mono, monospace)' }}>
                            {job.progress_phase}{job.progress_step ? ` · ${job.progress_step}` : ''}
                          </span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell
                      style={{ ...tdBase, fontFamily: 'var(--mono, monospace)', color: 'var(--text-secondary)', fontSize: 13 }}
                      title={`enqueued ${new Date(job.enqueued_at).toLocaleString()}${job.reported_at ? ` · reported ${new Date(job.reported_at).toLocaleString()}` : ''}`}
                    >
                      {dur || '—'}
                    </TableCell>
                    <TableCell style={{ ...tdBase, color: 'var(--text-muted)' }}>{relativeTime(job.enqueued_at)}</TableCell>
                    <TableCell style={{ ...tdBase, maxWidth: 240 }}>
                      {parsed ? (
                        <span style={{
                          display: 'block', overflow: 'hidden', textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap', fontFamily: 'var(--mono, monospace)',
                          color: failResult ? 'var(--ps-pill-red-tx)' : 'var(--text-secondary)',
                        }}>
                          {parsed.summary}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>—</span>
                      )}
                    </TableCell>
                    <TableCell style={{ ...tdBase, textAlign: 'center' }}>
                      {isExp
                        ? <ChevronUp size={14} color="var(--text-muted)" />
                        : <ChevronDown size={14} color="var(--text-muted)" />}
                    </TableCell>
                  </TableRow>
                  {isExp && (
                    <TableRow style={{ background: 'var(--surface-sunken)' }}>
                      <TableCell colSpan={7} style={{ padding: '4px 16px 16px', borderBottom: '1px solid var(--border)' }}>
                        <JobStepper job={job} progress={progressById[job.id] || []} loading={progressLoading === job.id} />
                        {(() => {
                          const g = gatedInfo(job);
                          return g.skipped && g.reason ? (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: 'var(--bg-muted)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                              <Ban size={14} color="var(--ps-pill-gray-tx)" style={{ flexShrink: 0, marginTop: 1 }} />
                              <span><strong>Skipped (gated no-op):</strong> {g.reason}</span>
                            </div>
                          ) : null;
                        })()}
                        {(() => {
                          const vf = jobFindings(job);
                          return vf ? (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                <ShieldAlert size={13} color="var(--ps-pill-red-tx)" />
                                <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)' }}>
                                  Vulnerabilities — showing {vf.findings.length} of {vf.total} critical/high{vf.truncated ? ' (truncated)' : ''}
                                </span>
                              </div>
                              <FindingsTable findings={vf.findings} />
                            </div>
                          ) : null;
                        })()}
                        {parsed && (
                        <div style={{
                          background: failResult ? 'var(--ps-pill-red-bg)' : 'var(--bg-muted)',
                          border: `1px solid ${failResult ? 'var(--ps-pill-red-bg)' : 'var(--border)'}`,
                          borderRadius: 8, padding: '12px 14px',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            {failResult
                              ? <AlertCircle size={13} color="var(--ps-pill-red-tx)" />
                              : <Terminal size={13} color="var(--text-secondary)" />}
                            <span style={{ fontSize: 11.5, fontWeight: 600, color: failResult ? 'var(--ps-pill-red-tx)' : 'var(--text-secondary)', fontFamily: 'var(--mono, monospace)' }}>
                              {parsed.playbook || job.op_key}
                              {parsed.rc != null ? ` · rc=${parsed.rc}` : ''}
                              {dur ? ` · ${dur}` : ''}
                            </span>
                          </div>
                          <pre style={{
                            fontSize: 11.5, fontFamily: 'var(--mono, monospace)',
                            color: failResult ? 'var(--ps-pill-red-tx)' : 'var(--text-primary)',
                            lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0,
                          }}>{parsed.full}</pre>
                        </div>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────
export function RemoteOpsPage() {
  const [searchParams] = useSearchParams();
  const initialTarget = searchParams.get('instance') || undefined;
  const [catalog, setCatalog] = useState<CatalogOp[]>([]);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [runners, setRunners] = useState<RunnerStatus[]>([]);
  const [live, setLive] = useState(true);

  const load = useCallback(() => {
    apiRequest<CatalogOp[]>('/operations/catalog').then((c) => setCatalog(c || [])).catch(() => setCatalog([]));
    apiRequest<Instance[]>('/fleet/instances').then((i) => setInstances(i || [])).catch(() => setInstances([]));
    apiRequest<Job[]>('/operations/jobs').then((j) => setJobs(j || [])).catch(() => setJobs([]));
    apiRequest<RunnerStatus[]>('/operations/runners').then((r) => setRunners(r || [])).catch(() => setRunners([]));
  }, []);
  useEffect(load, [load]);

  // Live auto-refresh: re-poll jobs + runner health on a short cadence so a stuck op,
  // a streaming pipeline, or a runner going down is visible without a manual refresh.
  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => {
      apiRequest<Job[]>('/operations/jobs').then((j) => setJobs(j || [])).catch(() => { /* keep last */ });
      apiRequest<RunnerStatus[]>('/operations/runners').then((r) => setRunners(r || [])).catch(() => { /* keep last */ });
    }, 5000);
    return () => clearInterval(id);
  }, [live]);

  return (
    <div style={{ padding: '24px 32px' }}>
      <style>{SPIN_KEYFRAMES}</style>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Terminal size={22} />
          <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-.01em', margin: 0 }}>Remote Operations</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={() => setLive((v) => !v)}
            style={{ ...btnSec, color: live ? 'var(--ps-pill-green-tx)' : 'var(--text-secondary)' }}
            title={live ? 'Live updates on — click to pause' : 'Live updates paused — click to resume'}
          >
            {live ? <Activity size={13} style={spinStyle} /> : <Pause size={13} />}
            {live ? 'Live' : 'Paused'}
          </button>
          <button onClick={load} style={btnSec}><RefreshCw size={13} /> Refresh</button>
        </div>
      </div>
      <RunnerHealthCard runners={runners} />
      <DispatchPanel catalog={catalog} instances={instances} jobs={jobs} onDispatched={load} initialTarget={initialTarget} />
      <OpCatalog catalog={catalog} jobs={jobs} />
      <JobHistory jobs={jobs} runners={runners} />
    </div>
  );
}
